/**
 * @fileoverview The mail-item lifecycle (`mail_items.status`) — the CUSTOMER-PICKUP axis: a package
 * or letter held at the store for a mailbox holder. Its vocabulary, its status machine and its badge
 * tones, declared once (LEAVE-CANON-001 R-74, owner ruling on Q-R14-22).
 *
 * ## Why it lives here
 *
 * The owner ruled customer pickup its OWN axis on the mailbox side, never a shipment status: a
 * shipment is an outbound parcel, and "is it ready for me to collect?" is a different question. The
 * axis already existed — `mail_items`, with pos-api's `MailItem` model, its routes, its location
 * scope and a DB CHECK (MAILBOX-RECON-001 E2) — but its vocabulary was spelled in pos-api's route
 * module (the values AND the machine) and again in pos-web's Check Mail tab (the badge tones and the
 * filter options). It lives here now, beside the shipment lifecycle, and both apps import it.
 *
 * ## The states
 *
 * - `received` — logged at the counter and held for the holder ("Awaiting Pickup" on screen). The
 *   holder's TEXT is not a status: `MailArrivalNotificationService` stamps the `customerNotified` /
 *   `notifiedAt` pair, so there is no `notified` (or `ready`) state to fall out of step with it.
 * - `picked_up` — released to a person; the route requires `pickedUpBy` (chain of custody) and the
 *   model stamps `pickedUpAt`.
 * - `returned` — sent back to the carrier (PostalMate's "Returned to carrier" and "Rejected by
 *   customer"). Terminal. A released item can still be recorded returned (`picked_up → returned`),
 *   exactly the machine the route enforced.
 * - `abandoned` — never collected and not sent back (PostalMate's "Abandoned" and "Hold date
 *   expired"). Terminal (LEAVE-CANON-001 R-78).
 * - `missing` — logged at the counter and now nowhere to be found (PostalMate's "Missing"). The only
 *   way out is back on the shelf (`missing → received`) when it turns up (R-78).
 *
 * Every status is a KEY, a terminal one mapping to `[]` — the `SHIPMENT_STATUS_TRANSITIONS`
 * convention, so a reader can list a status's targets without knowing which convention was used.
 *
 * Q-R14-26 (R-76 / R-77 / R-78) converged customer pickup onto this ONE axis: the PostalMate import's
 * `package_arrivals` is absorbed into `mail_items` and retired, and the shipment intake type
 * `package_hold` is retired. There is no `forwarded`: PostalMate never recorded one — the rows the old
 * import called forwarded were returned-to-carrier.
 *
 * ## What a mail item IS
 *
 * `mail_items.type` (a letter, a package, …) lives here too (L-R7CLOSE row 54 item 11, MAILBOX-SIGNUP-JOURNEY-001
 * C2b), the same way and for the same reason: it was spelled in pos-api's intake route, again in pos-web's validator,
 * its intake drawer and a dead constant, and a FIFTH, different list in pos-web's mock generator. The values are
 * exactly what `mail_items`' type CHECK admits (pos `20260617120000`, frozen history).
 *
 * @module @puffin-core/status-taxonomy/mailItemStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/**
 * What a mail item is, in intake order — exactly what `mail_items`' type CHECK admits.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAIL_ITEM_TYPE = Object.freeze({
  LETTER: 'letter',
  PACKAGE: 'package',
  LARGE_PACKAGE: 'large_package',
  EXPRESS: 'express',
  CERTIFIED: 'certified',
  REGISTERED: 'registered',
});

/**
 * Every type, in intake order — what the intake route accepts and the drawer offers.
 *
 * @type {ReadonlyArray<string>}
 */
const MAIL_ITEM_TYPE_VALUES = Object.freeze(Object.values(MAIL_ITEM_TYPE));

/**
 * The mail-item lifecycle, in lifecycle order — exactly what `mail_items`' CHECK admits.
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAIL_ITEM_STATUS = Object.freeze({
  RECEIVED: 'received',
  PICKED_UP: 'picked_up',
  RETURNED: 'returned',
  ABANDONED: 'abandoned',
  MISSING: 'missing',
});

/**
 * Every status, in lifecycle order — what the create / status routes accept and the filter offers.
 *
 * @type {ReadonlyArray<string>}
 */
const MAIL_ITEM_STATUS_VALUES = Object.freeze(Object.values(MAIL_ITEM_STATUS));

/**
 * Every legal transition, spelled once.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const MAIL_ITEM_STATUS_TRANSITIONS = Object.freeze({
  [MAIL_ITEM_STATUS.RECEIVED]: Object.freeze([
    MAIL_ITEM_STATUS.PICKED_UP,
    MAIL_ITEM_STATUS.RETURNED,
    MAIL_ITEM_STATUS.ABANDONED,
    MAIL_ITEM_STATUS.MISSING,
  ]),
  [MAIL_ITEM_STATUS.PICKED_UP]: Object.freeze([MAIL_ITEM_STATUS.RETURNED]),
  [MAIL_ITEM_STATUS.RETURNED]: Object.freeze([]),
  [MAIL_ITEM_STATUS.ABANDONED]: Object.freeze([]),
  [MAIL_ITEM_STATUS.MISSING]: Object.freeze([MAIL_ITEM_STATUS.RECEIVED]),
});

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * The statuses a mail item at `status` may move to next.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as
 * `constructor`) answers `[]` rather than a function.
 *
 * @param {string} status - The item's current status.
 * @returns {ReadonlyArray<string>} The legal targets; `[]` when terminal or unknown.
 */
function mailItemStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(MAIL_ITEM_STATUS_TRANSITIONS, status)
    ? MAIL_ITEM_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * Each status's badge TONE — the map `<StatusBadgeCell statusTones>` takes (Q-MI-25: the tone rides
 * `data-tone` on the one tone pill, never a class). An item waiting at the counter needs attention
 * (`warning`), a collected one is the happy end (`success`), a returned or abandoned one is closed
 * without a verdict (`neutral`), and a missing one needs someone to look for it (`danger`).
 *
 * @type {Readonly<Object<string, string>>}
 */
const MAIL_ITEM_STATUS_BADGE_TONES = Object.freeze({
  [MAIL_ITEM_STATUS.RECEIVED]: BADGE_TONE.WARNING,
  [MAIL_ITEM_STATUS.PICKED_UP]: BADGE_TONE.SUCCESS,
  [MAIL_ITEM_STATUS.RETURNED]: BADGE_TONE.NEUTRAL,
  [MAIL_ITEM_STATUS.ABANDONED]: BADGE_TONE.NEUTRAL,
  [MAIL_ITEM_STATUS.MISSING]: BADGE_TONE.DANGER,
});

module.exports = {
  MAIL_ITEM_TYPE,
  MAIL_ITEM_TYPE_VALUES,
  MAIL_ITEM_STATUS,
  MAIL_ITEM_STATUS_VALUES,
  MAIL_ITEM_STATUS_TRANSITIONS,
  MAIL_ITEM_STATUS_BADGE_TONES,
  mailItemStatusTargets,
};
